import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Box } from 'rebass/styled-components';
import { Share, ExternalLink, Download, Edit2, RefreshCw, Copy, Star, Eye, X, Contact, Rocket } from 'lucide-react';
import { convertQueryToString } from 'helpers/queryString';
import { trackEvent } from 'helpers/analytics';
import { SparkleIcon } from '../components/SparkleIcon';
import { getJobBoostStatus } from '../jobBoostStatus';

export const useActionItems = ({
  jobActionsLoading,
  isJobSourcePostedActiveOrExpired,
  getJobById,
  jobActions,
  activeNetwork,
  source,
  isNetworkAdmin,
  setSelectedJob,
  isCompanyProfile,
  showApplicants,
  onFindCandidates,
  onOpenProject,
  onLaunchApplicationBoostAgent,
}) => {
  const history = useHistory();
  return useMemo(
    () =>
      [
        {
          rowActionKey: 'people_finder',
          title: (job) => (job.findemProjectId ? 'Open project' : 'Find candidates'),
          children: (job) => {
            const label = job.findemProjectId ? 'Open project' : 'Find candidates';
            return (
              <>
                <Box mr={2} height="16px" width="16px" aria-hidden="true">
                  <SparkleIcon />
                </Box>
                {label}
              </>
            );
          },
          onClick: (e) => {
            const job = getJobById(e);
            if (job.findemProjectId) {
              onOpenProject(job);
            } else {
              onFindCandidates(job);
            }
          },
          onlyNetworkAdmin: true,
          hidden: isCompanyProfile,
        },
        {
          rowActionKey: 'boost_applications',
          title: 'Boost applications',
          children: (
            <>
              <Box mr={2} height="16px" width="16px" strokeWidth="1.5" aria-hidden="true" as={Rocket} />
              Boost applications
            </>
          ),
          onClick: (e) => {
            const job = getJobById(e);
            const status = getJobBoostStatus(job, onLaunchApplicationBoostAgent);
            if (status.action) {
              status.action(job);
            }
          },
          isDisabled: (e) => {
            const job = getJobById(e);
            const status = getJobBoostStatus(job, onLaunchApplicationBoostAgent);
            if (['hidden', 'not_visible', 'hidden_forced'].includes(job.visibility)) {
              return true;
            }
            if (['executive', 'cxo', 'vice_president'].includes(job.seniority)) {
              return true;
            }
            return !status.action;
          },
          onlyNetworkAdmin: true,
          hidden: isCompanyProfile || !activeNetwork.features?.includes('sourcing_agent'),
        },
        {
          rowActionKey: 'sep_after_boost',
          Item: Box,
          px: 3,
          children: <Box as="hr" variant="hr" />,
          hidden: isCompanyProfile || !isNetworkAdmin,
        },
        ...(showApplicants
          ? [
              {
                rowActionKey: 'view_applicants',
                isDisabled: (e) => {
                  const job = getJobById(e);
                  if (job.source === 'job_source') {
                    return true;
                  }

                  return job.jobApplicantsCount === 0;
                },
                isLoading: jobActionsLoading.isViewing,
                title: 'View applicants',
                children: (
                  <>
                    <Box mr={2} height="16px" width="16px" strokeWidth="1.5" aria-hidden="true" as={Contact} />
                    View applicants
                  </>
                ),
                onClick: (e) => {
                  const job = getJobById(e);
                  trackEvent('jobs:see_applicants', { jobId: job.id, numberApplicants: job.jobApplicantsCount });
                  history.push(
                    `/networks/${activeNetwork.slug}/contacts?${convertQueryToString({
                      filters: {
                        jobApplicationsJobs: [
                          {
                            value: job.id,
                            label: job.title,
                          },
                        ],
                      },
                    })}`,
                  );
                },
                onlyNetworkAdmin: false,
                hidden: isCompanyProfile,
              },
            ]
          : []),
        {
          rowActionKey: 'export_applicants',
          isLoading: jobActionsLoading.isRequestingApplicationsDownload,
          title: 'Export applicants',
          children: (
            <>
              <Box mr={2} height="16px" width="16px" strokeWidth="1.5" aria-hidden="true" as={Download} />
              Export applicants
            </>
          ),
          onClick: (e) => {
            const job = getJobById(e);
            jobActions.onDownloadJobApplications(job);
          },
          isDisabled: (e) => {
            const job = getJobById(e);
            return !job.jobApplicantsCount;
          },
          onlyNetworkAdmin: false,
        },
        {
          rowActionKey: 'sep_export_share',
          Item: Box,
          px: 3,
          children: <Box as="hr" variant="hr" />,
          hidden: isCompanyProfile,
        },
        {
          rowActionKey: 'share',
          isDisabled: (e) => {
            const job = getJobById(e);
            return !(job.status === 'active');
          },
          title: 'Share',
          children: (
            <>
              <Box mr={2} height="16px" width="16px" strokeWidth="1.5" aria-hidden="true" as={Share} />
              Share
            </>
          ),
          onClick: (e) => {
            const job = getJobById(e);
            setSelectedJob(job);
            jobActions.onShareJob(job);
          },
          onlyNetworkAdmin: false,
          hidden: isCompanyProfile,
        },
        {
          rowActionKey: 'view_job_board',
          isDisabled: (e) => {
            const job = getJobById(e);
            return !(job.status === 'active');
          },
          isLoading: jobActionsLoading.isViewing,
          title: 'View on job board',
          children: (
            <>
              <Box mr={2} height="16px" width="16px" strokeWidth="1.5" aria-hidden="true" as={ExternalLink} />
              View on job board
            </>
          ),
          onClick: (e) => {
            const job = getJobById(e);
            jobActions.onViewJob({ job });
          },
          onlyNetworkAdmin: false,
          hidden: isCompanyProfile,
        },
        {
          rowActionKey: 'set_featured_status',
          isLoading: jobActionsLoading.isFeaturingJob,
          title: 'Set featured status',
          children: (
            <>
              <Box mr={2} height="16px" width="16px" strokeWidth="1.5" aria-hidden="true" as={Star} />
              Set featured status
            </>
          ),
          onClick: (e) => {
            const job = getJobById(e);
            setSelectedJob(job);
            jobActions.onFeatureJobRequest(job);
          },
          isDisabled: (e) => {
            const job = getJobById(e);
            return job.status !== 'active' || job.visibility?.includes?.('hidden');
          },
          onlyNetworkAdmin: true,
          hidden: isCompanyProfile,
        },
        {
          rowActionKey: 'set_visibility',
          isLoading: jobActionsLoading.isForcingVisibility,
          title: 'Set visibility',
          children: (
            <>
              <Box mr={2} height="16px" width="16px" strokeWidth="1.5" aria-hidden="true" as={Eye} />
              Set visibility
            </>
          ),
          onClick: (e) => {
            const job = getJobById(e);
            setSelectedJob(job);
            jobActions.onForceJobVisibilityRequest(job);
          },
          isDisabled: (e) => {
            const job = getJobById(e);
            return job.status !== 'active' || !isNetworkAdmin;
          },
          onlyNetworkAdmin: true,
          hidden: isCompanyProfile,
        },
        {
          rowActionKey: 'sep_visibility_edit',
          Item: Box,
          px: 3,
          children: <Box as="hr" variant="hr" />,
          hidden: isCompanyProfile,
        },
        {
          rowActionKey: 'edit',
          isDisabled: (e) => {
            const job = getJobById(e);
            return !(job.status === 'active' && job.source === 'posted');
          },
          isLoading: jobActionsLoading.isEditing,
          title: 'Edit',
          children: (
            <>
              <Box mr={2} height="16px" width="16px" strokeWidth="1.5" aria-hidden="true" as={Edit2} />
              Edit
            </>
          ),
          onClick: (e) => {
            const job = getJobById(e);
            if (source === 'employer_profile') {
              history.push(
                `/networks/${activeNetwork.slug}/companies/${job.organization.id}-${job.organization.slug}/edit-job/${job.id}`,
              );
            } else {
              history.push(`/networks/${activeNetwork.slug}/jobs/edit-job/${job.id}`);
            }
          },
          onlyNetworkAdmin: false,
        },
        {
          rowActionKey: 'extend',
          isDisabled: isJobSourcePostedActiveOrExpired,
          isLoading: jobActionsLoading.isExtending,
          title: 'Extend',
          children: (
            <>
              <Box mr={2} height="16px" width="16px" strokeWidth="1.5" aria-hidden="true" as={RefreshCw} />
              Extend
            </>
          ),
          onClick: (e) => {
            const job = getJobById(e);
            setSelectedJob(job);
            jobActions.onExtendJobRequest(job);
          },
          onlyNetworkAdmin: false,
        },
        {
          rowActionKey: 'duplicate',
          title: 'Duplicate',
          children: (
            <>
              <Box mr={2} height="16px" width="16px" strokeWidth="1.5" aria-hidden="true" as={Copy} />
              Duplicate
            </>
          ),
          onClick: (e) => {
            const job = getJobById(e);
            if (source === 'employer_profile') {
              history.push(
                `/networks/${activeNetwork.slug}/companies/${job.organization.slug}/duplicate-job/${job.id}`,
              );
            } else {
              history.push(`/networks/${activeNetwork.slug}/jobs/duplicate-job/${job.id}`);
            }
          },
          onlyNetworkAdmin: false,
        },
        {
          rowActionKey: 'sep_duplicate_close',
          Item: Box,
          px: 3,
          children: <Box as="hr" variant="hr" />,
        },
        {
          rowActionKey: 'close',
          isLoading: jobActionsLoading.isClosing,
          title: 'Close',
          sx: {
            color: 'text.error',
            '&:hover:not(:disabled)': { color: 'text.error' },
          },
          children: (
            <>
              <Box
                mr={2}
                height="16px"
                width="16px"
                strokeWidth="1.5"
                aria-hidden="true"
                as={X}
                sx={{ stroke: 'currentColor' }}
              />
              Close
            </>
          ),
          onClick: (e) => {
            const job = getJobById(e);
            setSelectedJob(job);
            jobActions.onCloseJobRequest(job);
          },
          isDisabled: (e) => {
            const job = getJobById(e);
            if (job.source === 'posted') {
              if (job.status === 'expired') {
                return true;
              }
              if (job.status === 'active') {
                return false;
              }
            }
            return true;
          },
          onlyNetworkAdmin: false,
        },
      ].filter(({ onlyNetworkAdmin, hidden }) => (isNetworkAdmin || !onlyNetworkAdmin) && !hidden),
    [
      jobActionsLoading.isViewing,
      jobActionsLoading.isRequestingApplicationsDownload,
      jobActionsLoading.isEditing,
      jobActionsLoading.isExtending,
      jobActionsLoading.isFeaturingJob,
      jobActionsLoading.isForcingVisibility,
      jobActionsLoading.isClosing,
      isJobSourcePostedActiveOrExpired,
      getJobById,
      setSelectedJob,
      jobActions,
      source,
      history,
      activeNetwork.slug,
      activeNetwork.features,
      isNetworkAdmin,
      isCompanyProfile,
      showApplicants,
      onFindCandidates,
      onOpenProject,
      onLaunchApplicationBoostAgent,
    ],
  );
};
